import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { CreditCard } from "lucide-react";
import THEME from "./theme";

// Dashboard-Widget "RFID-Prüfliste" (CARE-Sync, nur Administratoren): offene Fälle der Bestands-Automatik
// direkt auf dem Dashboard ansehen und entscheiden (Backend: /api/care-sync/bestand/*, siehe
// routers_care_sync.py). Gleiche Absicherung wie die Prüfliste in den Einstellungen (RfidPruefliste.jsx):
// "Neues Band übernehmen" (ersetzen) zeigt vorab die Prüfpunkte und braucht eine Bestätigung.
// Aufbau wie die anderen Dashboard-Widgets (Kalender, Online Portal): Kopfzeile mit Symbol, Titel, Zähler-Badge
// und Auf-/Zuklappen (initialOffen/onOffenChange), den Rahmen liefert WidgetRahmen. Der Dialog läuft über ein
// Portal, weil das Raster (react-grid-layout) per transform positioniert und "position: fixed" sonst nicht am
// Fenster hängt. Nur maskierte RFID-Nummern. Bewusst OHNE Importe aus App.js (nur React + theme.js).

const API = ["localhost", "127.0.0.1"].includes(window.location.hostname)
  ? "http://localhost:8000"
  : `${window.location.protocol}//${window.location.hostname}`;
const apiFetch = (url, optionen = {}) => fetch(url, { ...optionen, credentials: "include" });

// handeln = braucht in der Regel eine Entscheidung von Hand; sonst nur Hinweis (z.B. Karte ist schon vorhanden)
const ARTEN = {
  WECHSEL: { label: "Wechsel", handeln: true },
  IDENTITAET_UNKLAR: { label: "Identität unklar", handeln: true },
  NICHT_BERECHTIGT: { label: "Nicht berechtigt", handeln: true },
  RFID_KONFLIKT: { label: "RFID-Konflikt", handeln: true },
  TTLOCK_ABWEICHUNG: { label: "TTLock-Abweichung", handeln: true },
  TTLOCK_FEHLT: { label: "TTLock fehlt", handeln: true },
  TYP_NICHT_UNTERSTUETZT: { label: "Typ nicht unterstützt", handeln: false },
  NICHT_AUTOMATIKFAEHIG: { label: "Nicht automatikfähig", handeln: false },
};
const brauchtEntscheidung = (art) => (ARTEN[art] || { handeln: true }).handeln;

export const START_TAB_SCHLUESSEL = "comefitEinstellungenStartTab";

function zeitText(iso) {
  if (!iso) return "–";
  const d = new Date(/(Z|[+-]\d\d:?\d\d)$/i.test(iso) ? iso : `${iso}Z`);
  if (isNaN(d.getTime())) return "–";
  return d.toLocaleString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) + " Uhr";
}

async function fehlertext(res, fallback) {
  if (res.status === 403) return "Nur für Administratoren.";
  const daten = await res.json().catch(() => ({}));
  return typeof daten.detail === "string" && daten.detail ? daten.detail : fallback;
}

const knopf = (hell) => ({ padding: "7px 12px", borderRadius: THEME.radius.md, background: hell ? THEME.colors.primary : THEME.colors.surfaceMuted, color: hell ? "#ffffff" : THEME.colors.text, fontSize: THEME.fontSize.sm, border: "none", cursor: "pointer", whiteSpace: "nowrap" });

function ArtEtikett({ art }) {
  const a = ARTEN[art] || { label: art, handeln: true };
  const farbe = a.handeln ? THEME.colors.warning : THEME.colors.textMuted;
  return <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: THEME.fontSize.xs, fontWeight: 700, color: farbe, border: `1px solid ${farbe}`, background: `${farbe}22`, whiteSpace: "nowrap" }}>{a.label}</span>;
}

export default function RfidPruefHinweis({ onPersonOeffnen, initialOffen, onOffenChange }) {
  const [faelle, setFaelle] = useState(null); // null = noch nicht geladen oder nicht ladbar
  const [geladen, setGeladen] = useState(false);
  const [offen, setOffen] = useState(!!initialOffen);
  const [meldung, setMeldung] = useState(null); // { typ: "ok" | "fehler", text }
  const [bestaetige, setBestaetige] = useState(null); // { id, entscheidung } fuer "Ablehnen"/"Erledigt" (zweistufig)
  const [dialog, setDialog] = useState(null); // { fall, pruefung, pruefungLaedt, pruefungFehler, sendet, fehler }
  const ladeZaehler = useRef(0);

  const laden = useCallback(async () => {
    const nr = ++ladeZaehler.current;
    try {
      const res = await apiFetch(`${API}/api/care-sync/bestand/faelle?status=offen`);
      if (nr !== ladeZaehler.current) return;
      if (!res.ok) { setFaelle(null); setGeladen(true); return; }
      const liste = await res.json();
      setFaelle(Array.isArray(liste) ? liste : null);
      setGeladen(true);
    } catch {
      if (nr === ladeZaehler.current) { setFaelle(null); setGeladen(true); }
    }
  }, []);

  useEffect(() => {
    laden();
    const timer = setInterval(laden, 5 * 60 * 1000);
    return () => { ladeZaehler.current += 1; clearInterval(timer); };
  }, [laden]);

  const name = (f) => f.person?.name || f.care_name || `Fall ${f.id}`;

  const entscheide = async (fall, entscheidung) => {
    setMeldung(null);
    try {
      const res = await apiFetch(`${API}/api/care-sync/bestand/faelle/${fall.id}/entscheiden`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ entscheidung }),
      });
      if (!res.ok) {
        const text = await fehlertext(res, "Die Entscheidung konnte nicht ausgeführt werden.");
        laden();
        return { ok: false, text: res.status === 409 ? `Nicht ausgeführt: ${text}` : text };
      }
      const erfolg = { ersetzen: "Neues Band übernommen. Die alte Karte bleibt in TTLock bestehen.", verwerfen: "Abgelehnt. Es wurde nichts geändert.", erledigt: "Als erledigt markiert." }[entscheidung];
      setMeldung({ typ: "ok", text: `${erfolg} (${name(fall)})` });
      laden();
      return { ok: true };
    } catch {
      return { ok: false, text: "Keine Verbindung zum Server." };
    }
  };

  const direkt = async (fall, entscheidung) => {
    setBestaetige(null);
    const r = await entscheide(fall, entscheidung);
    if (!r.ok) setMeldung({ typ: "fehler", text: `${name(fall)}: ${r.text}` });
  };

  const oeffneErsetzen = async (fall) => {
    setMeldung(null);
    setBestaetige(null);
    setDialog({ fall, pruefung: null, pruefungLaedt: true, pruefungFehler: "", sendet: false, fehler: "" });
    try {
      const res = await apiFetch(`${API}/api/care-sync/bestand/faelle/${fall.id}/pruefung`);
      if (!res.ok) {
        const text = await fehlertext(res, "Die Prüfung ist fehlgeschlagen.");
        setDialog(d => d && d.fall.id === fall.id ? { ...d, pruefungLaedt: false, pruefungFehler: text } : d);
        return;
      }
      const pruefung = await res.json();
      setDialog(d => d && d.fall.id === fall.id ? { ...d, pruefung, pruefungLaedt: false } : d);
    } catch {
      setDialog(d => d && d.fall.id === fall.id ? { ...d, pruefungLaedt: false, pruefungFehler: "Keine Verbindung zum Server." } : d);
    }
  };

  const ersetzenAusfuehren = async () => {
    if (!dialog || dialog.sendet || !dialog.pruefung?.moeglich) return;
    const fall = dialog.fall;
    setDialog(d => ({ ...d, sendet: true, fehler: "" }));
    const r = await entscheide(fall, "ersetzen");
    if (r.ok) setDialog(null);
    else setDialog(d => d ? { ...d, sendet: false, fehler: r.text } : d);
  };

  // Letzter Fall entschieden: Erfolgsmeldung noch kurz zeigen, danach verschwindet sie von selbst.
  useEffect(() => {
    if (!meldung || !faelle || faelle.length > 0) return undefined;
    const timer = setTimeout(() => setMeldung(null), 8000);
    return () => clearTimeout(timer);
  }, [meldung, faelle]);

  const toggleOffen = () => { const neu = !offen; setOffen(neu); onOffenChange?.(neu); };

  const liste = faelle || [];
  const wichtig = liste.filter(f => brauchtEntscheidung(f.art));
  const hinweise = liste.filter(f => !brauchtEntscheidung(f.art));
  const farbe = wichtig.length > 0 ? THEME.colors.warning : THEME.colors.primary;

  const zeile = (f) => {
    const wechsel = f.art === "WECHSEL";
    const fragt = bestaetige && bestaetige.id === f.id ? bestaetige.entscheidung : null;
    return (
      <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 0", borderTop: `1px solid ${THEME.colors.border}` }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {f.person && onPersonOeffnen ? (
              <span onClick={() => onPersonOeffnen(f.person.typ, f.person.id)} title="Profil öffnen" style={{ color: THEME.colors.primary, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>{name(f)}</span>
            ) : (
              <span style={{ color: THEME.colors.text, fontWeight: 600 }}>{name(f)}</span>
            )}
            <ArtEtikett art={f.art} />
          </div>
          <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 2, wordBreak: "break-word" }}>
            {wechsel && f.neues_band_maskiert
              ? <>Bisher <span style={{ fontFamily: "monospace" }}>{f.bisherige_rfid_maskiert || "–"}</span> → neues Band <span style={{ fontFamily: "monospace" }}>{f.neues_band_maskiert}</span> · </>
              : null}
            in CARE zugeordnet {zeitText(f.zugeordnet)}
            {!wechsel && f.gruende && f.gruende[0] ? ` · ${f.gruende[0]}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {fragt ? (
            <>
              <span style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, alignSelf: "center" }}>{fragt === "verwerfen" ? "Wirklich ablehnen?" : "Als erledigt markieren?"}</span>
              <button onClick={() => direkt(f, fragt)} style={knopf(true)}>Ja</button>
              <button onClick={() => setBestaetige(null)} style={knopf(false)}>Nein</button>
            </>
          ) : (
            <>
              {wechsel && (f.entscheidungen || []).includes("ersetzen") && <button onClick={() => oeffneErsetzen(f)} style={knopf(true)}>Neues Band übernehmen</button>}
              {!wechsel && (f.entscheidungen || []).includes("erledigt") && <button onClick={() => setBestaetige({ id: f.id, entscheidung: "erledigt" })} style={knopf(true)}>Erledigt</button>}
              {(f.entscheidungen || []).includes("verwerfen") && <button onClick={() => setBestaetige({ id: f.id, entscheidung: "verwerfen" })} style={knopf(false)}>Ablehnen</button>}
            </>
          )}
        </div>
      </div>
    );
  };

  const badge = wichtig.length > 0
    ? { text: `${wichtig.length} ${wichtig.length === 1 ? "wartet" : "warten"}`, farbe: THEME.colors.warning }
    : hinweise.length > 0 ? { text: `${hinweise.length} ${hinweise.length === 1 ? "Hinweis" : "Hinweise"}`, farbe: THEME.colors.textMuted } : null;

  return (
    <div style={{ background: THEME.colors.surface, borderRadius: THEME.radius.xl, padding: "1.75rem", borderLeft: `4px solid ${farbe}`, marginBottom: 16 }}>
      <div onClick={toggleOffen} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
        <h3 style={{ color: THEME.colors.text, margin: 0, fontSize: THEME.fontSize.xl, display: "flex", alignItems: "center", gap: 8 }}>
          <CreditCard size={18} color={farbe} /> RFID-Prüfliste
          {badge && <span style={{ background: badge.farbe, color: THEME.colors.text, fontSize: THEME.fontSize.xs, padding: "2px 8px", borderRadius: 10 }}>{badge.text}</span>}
        </h3>
        <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.xl }}>{offen ? "▲" : "▼"}</span>
      </div>

      {offen && (
        <div style={{ marginTop: 12 }}>
          {!geladen && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, margin: 0 }}>Lädt…</p>}
          {geladen && !faelle && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, margin: 0 }}>Die Prüfliste konnte nicht geladen werden.</p>}
          {faelle && faelle.length === 0 && !meldung && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.base, margin: 0 }}>Keine offenen Hinweise. 🎉</p>}
          {meldung && (
            <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 8, fontSize: THEME.fontSize.sm, color: meldung.typ === "ok" ? THEME.colors.success : THEME.colors.danger, background: `${meldung.typ === "ok" ? THEME.colors.success : THEME.colors.danger}22`, border: `1px solid ${meldung.typ === "ok" ? THEME.colors.success : THEME.colors.danger}` }}>
              {meldung.typ === "ok" ? "✓ " : ""}{meldung.text}{meldung.typ === "ok" && faelle && faelle.length === 0 ? " Die RFID-Prüfliste ist jetzt leer." : ""}
            </div>
          )}

          {liste.length > 0 && <div style={{ marginTop: 8 }}>{[...wichtig, ...hinweise].map(zeile)}</div>}
        </div>
      )}

      {dialog && createPortal(
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} onClick={() => { if (!dialog.sendet) setDialog(null); }}>
          <div style={{ background: THEME.colors.surface, borderRadius: THEME.radius.xl, padding: "1.5rem", width: "min(560px, 100%)", maxHeight: "90vh", overflowY: "auto", boxSizing: "border-box" }} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 style={{ color: THEME.colors.text, margin: "0 0 6px", fontSize: THEME.fontSize.xl }}>Neues Band übernehmen?</h3>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base, margin: "0 0 12px" }}>{name(dialog.fall)} · <ArtEtikett art={dialog.fall.art} /></p>
            <div style={{ background: `${THEME.colors.warning}18`, border: `1px solid ${THEME.colors.warning}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
              <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, margin: 0 }}>
                Die neue RFID wird beim Mitglied eingetragen und es wird eine neue TTLock-Karte angelegt. Die alte Karte bleibt in TTLock bestehen und muss bei Bedarf von Hand entfernt werden.
              </p>
            </div>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, margin: "0 0 4px" }}>
              Bisherige RFID: <span style={{ color: THEME.colors.text, fontFamily: "monospace" }}>{dialog.fall.bisherige_rfid_maskiert || "–"}</span> → Neues Band: <span style={{ color: THEME.colors.text, fontFamily: "monospace" }}>{dialog.fall.neues_band_maskiert || "–"}</span>
            </p>
            <p style={{ color: THEME.colors.text, fontSize: THEME.fontSize.base, fontWeight: 600, margin: "12px 0 6px" }}>Prüfpunkte</p>
            {dialog.pruefungLaedt && <p style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>Prüfe…</p>}
            {dialog.pruefungFehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm }}>{dialog.pruefungFehler}</p>}
            {dialog.pruefung && (
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px" }}>
                {(dialog.pruefung.punkte || []).map((p, i) => (
                  <li key={i} style={{ color: p.ok ? THEME.colors.success : THEME.colors.danger, fontSize: THEME.fontSize.sm, marginBottom: 3 }}>{p.ok ? "✓" : "✗"} {p.pruefung}</li>
                ))}
              </ul>
            )}
            {dialog.pruefung && !dialog.pruefung.moeglich && (
              <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>
                Übernehmen ist gerade nicht möglich. Sie können den Fall ablehnen und das Band im Mitgliedsprofil unter „RFID bearbeiten" von Hand zuordnen.
              </p>
            )}
            {dialog.fehler && <p style={{ color: THEME.colors.danger, fontSize: THEME.fontSize.sm, margin: "0 0 12px" }}>{dialog.fehler}</p>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button onClick={() => setDialog(null)} disabled={dialog.sendet} style={knopf(false)}>Abbrechen</button>
              <button onClick={ersetzenAusfuehren} disabled={dialog.sendet || dialog.pruefungLaedt || !dialog.pruefung?.moeglich} style={{ ...knopf(true), opacity: dialog.sendet || dialog.pruefungLaedt || !dialog.pruefung?.moeglich ? 0.5 : 1 }}>
                {dialog.sendet ? "Übernehme…" : "Jetzt übernehmen"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
